/* eslint-disable import/prefer-default-export */
import geocode from 'lib/location/geocode';
import GoogleGeocode from 'lib/location/Locations/Google';
import MaxMindGeolocation from 'lib/location/Locations/MaxMind';
import Geocode from 'types/Geocode';
import { IncomingMessage } from 'http';
import getIpAddress from 'lib/getIpAddress';
import { serverSideMaxMindGeolocate } from 'lib/location/MaxMind';
import { fetchMaxMindGeolocation } from 'lib/MaxMindApi';

enum Methods {
  Maxmind = 'maxmind',
  Html5 = 'html5',
  Google = 'google',
}

export interface LocationData {
  city: string;
  latitude: number;
  longitude: number;
  stateAbbreviation: string;
  zip: string;
}

export type LatLon =
  | {
      readonly latitude: number;
      readonly longitude: number;
    }
  | {
      readonly latitude: null;
      readonly longitude: null;
    };

export interface LocationSuccessInterface {
  (geocode: Geocode): void;
}

export type RequestType = IncomingMessage & {
  cookies: Partial<{
    [key: string]: string;
  }>;
};
export async function geolocateRequest(request: RequestType): Promise<LatLon | undefined> {
  const ip = await getIpAddress(request);
  let maxmindResult;
  if (ip) maxmindResult = await serverSideMaxMindGeolocate(ip);

  return maxmindResult;
}

function getPosition(options?: Record<string, any>): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    // https://developer.mozilla.org/en-US/docs/Web/API/Geolocation
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

async function getCoordsFromBrowser(): Promise<GeolocationCoordinates> {
  const position = await getPosition();

  return position.coords;
}

async function reverseGeocode(coordinates: {
  latitude: number;
  longitude: number;
}): Promise<google.maps.GeocoderResult[]> {
  const location = {
    lat: coordinates.latitude,
    lng: coordinates.longitude,
  };
  return geocode({ location });
}

function cacheInLegacyStorage(method: Methods, location: Geocode): void {
  const userID = localStorage.getItem('User');
  const localStorageKey = `User-${userID}`;

  if (!userID) {
    // No key to cache on
    return;
  }

  const rawUserData = localStorage.getItem(localStorageKey);

  const userData = rawUserData ? JSON.parse(rawUserData) : {};

  const newUserData = Object.assign({}, userData, {
    [`${method}_data`]: location,
  });

  localStorage.setItem(localStorageKey, JSON.stringify(newUserData));
}

async function withCache<T>(key: string, compute: () => Promise<T>): Promise<T> {
  const cachedVal = localStorage.getItem(key);
  if (cachedVal) {
    return JSON.parse(cachedVal) as T;
  }
  const computedVal = await compute();
  localStorage.setItem(key, JSON.stringify(computedVal));
  return computedVal;
}

export async function maxMindGeolocate(): Promise<Geocode> {
  let res;

  // try fetching maxmind location with geoip2 script tag
  // fallback to api call if script tag fails
  try {
    // http://dev.maxmind.com/geoip/geoip2/javascript/
    res = await withCache('maxmind_location', () => new Promise(geoip2.city));
  } catch (e) {
    res = await fetchMaxMindGeolocation();
  }

  const location = new MaxMindGeolocation(res);
  cacheInLegacyStorage(Methods.Maxmind, location);

  return location;
}

export async function html5Geolocate(): Promise<Geocode> {
  const googleData = await withCache('html5_location', async () => {
    const coordinates = await getCoordsFromBrowser();
    return reverseGeocode(coordinates);
  });
  const location = new GoogleGeocode(googleData[0]);
  cacheInLegacyStorage(Methods.Html5, location);

  return location;
}

export async function clientSideGeolocate(): Promise<Geocode> {
  try {
    const maxmindLocation = await maxMindGeolocate();
    return maxmindLocation;
  } catch (e) {
    return html5Geolocate();
  }
}
